import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Mail, Send, RefreshCw, AlertTriangle, Users, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authAxios = axios.create({ withCredentials: true });

const PLAN_COLORS = {
  Gratuito: 'bg-red-500/20 text-red-300 border-red-500/40',
  Destacado: 'bg-teal-500/20 text-teal-300 border-teal-500/40',
  Premium: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  Elite: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
};

export default function AdminWeeklyReportCenters() {
  const [days, setDays] = useState(14);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [confirmSend, setConfirmSend] = useState(false);

  const loadPreview = async (d = days) => {
    setLoading(true);
    setError('');
    setSendResult(null);
    try {
      const res = await authAxios.get(`${API}/admin/centers/weekly-report/preview?days=${d}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPreview(14); /* eslint-disable-next-line */ }, []);

  const runDryRun = async () => {
    setSending(true);
    setSendResult(null);
    try {
      const res = await authAxios.post(`${API}/admin/centers/weekly-report/send?days=${days}&dry_run=true`);
      setSendResult({ ...res.data, mode: 'dry_run' });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const runRealSend = async () => {
    if (!confirmSend) {
      setConfirmSend(true);
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const res = await authAxios.post(`${API}/admin/centers/weekly-report/send?days=${days}&dry_run=false`);
      setSendResult({ ...res.data, mode: 'real' });
      setConfirmSend(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-weekly-report-centers">
      <Card className="bg-slate-900 border-teal-800/50">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-slate-50 flex items-center gap-2">
            <Mail className="w-5 h-5 text-teal-400" />
            Reporte semanal a centros · Preview antes de enviar
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <p className="text-sm text-slate-400">
            Envía a cada centro sus métricas de los últimos N días con mensajes de upsell
            específicos por plan. Los correos NO se envían hasta que confirmes explícitamente.
            Remitente: <code className="bg-slate-800 px-1.5 py-0.5 rounded text-amber-300">{data?.sender_from || 'Ricardo Manzur <ricardo.manzur@sinadicciones.org>'}</code> ·
            WSP: <span className="text-emerald-400 font-mono">{data?.wsp_display || '+56 9 2834 1147'}</span>
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-slate-300">Ventana:</label>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="bg-slate-800 border border-slate-700 text-slate-100 rounded px-3 py-1.5 text-sm"
              data-testid="weekly-report-days-select"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={14}>Últimos 14 días</option>
              <option value={30}>Últimos 30 días</option>
            </select>
            <Button
              onClick={() => loadPreview()}
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-200 hover:bg-slate-800"
              disabled={loading}
              data-testid="weekly-report-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Recargar preview
            </Button>
          </div>

          {error && (
            <div className="bg-red-900/40 border border-red-700/50 text-red-200 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-800/60 border border-slate-700 rounded p-3">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Elegibles</div>
                <div className="text-2xl font-bold text-emerald-400" data-testid="weekly-eligible-count">
                  {data.eligible_centers}
                </div>
                <div className="text-xs text-slate-500 mt-1">Con email + actividad</div>
              </div>
              <div className="bg-slate-800/60 border border-slate-700 rounded p-3">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Sin email</div>
                <div className="text-2xl font-bold text-amber-400">
                  {data.skipped_no_email_with_activity}
                </div>
                <div className="text-xs text-slate-500 mt-1">Tienen actividad · no reciben</div>
              </div>
              <div className="bg-slate-800/60 border border-slate-700 rounded p-3">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Sin actividad</div>
                <div className="text-2xl font-bold text-slate-400">
                  {data.skipped_no_activity}
                </div>
                <div className="text-xs text-slate-500 mt-1">Skipped esta ronda</div>
              </div>
              <div className="bg-slate-800/60 border border-slate-700 rounded p-3">
                <div className="text-xs text-slate-400 uppercase tracking-wide">Ventana</div>
                <div className="text-2xl font-bold text-teal-400">{data.days}d</div>
                <div className="text-xs text-slate-500 mt-1">Últimos {data.days} días</div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={runDryRun}
              disabled={loading || sending || !data}
              className="bg-slate-700 hover:bg-slate-600"
              data-testid="weekly-report-dry-run-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              Simular envío (dry-run)
            </Button>
            <Button
              onClick={runRealSend}
              disabled={loading || sending || !data || !data.eligible_centers}
              className={confirmSend ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="weekly-report-send-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? 'Enviando...' : confirmSend ? `⚠️ CONFIRMAR envío a ${data?.eligible_centers} centros` : 'Enviar ahora'}
            </Button>
            {confirmSend && (
              <Button
                onClick={() => setConfirmSend(false)}
                variant="outline"
                className="border-slate-700 text-slate-200"
              >
                Cancelar
              </Button>
            )}
          </div>

          {sendResult && (
            <div className="bg-slate-800/60 border border-teal-700/50 rounded p-4 text-sm text-slate-200 space-y-1">
              <div className="font-bold text-teal-400 mb-2">
                {sendResult.mode === 'dry_run' ? '🧪 Resultado dry-run' : '✅ Envío completado'}
              </div>
              <div>Total centros: <strong>{sendResult.total_centers}</strong></div>
              <div>Elegibles: <strong>{sendResult.eligible}</strong></div>
              {sendResult.mode === 'real' && (
                <>
                  <div>Enviados: <strong className="text-emerald-400">{sendResult.sent}</strong></div>
                  <div>Fallidos: <strong className="text-red-400">{sendResult.failed}</strong></div>
                </>
              )}
              <div>Sin email: <strong>{sendResult.skipped_no_email}</strong></div>
              <div>Sin actividad: <strong>{sendResult.skipped_no_activity}</strong></div>
              <div>Ya enviados (dedupe {`<`}6d): <strong>{sendResult.skipped_dedupe}</strong></div>
              <div className="pt-2 flex gap-3 flex-wrap">
                {Object.entries(sendResult.by_plan || {}).map(([p, n]) => (
                  <span key={p} className={`px-2.5 py-1 rounded text-xs border ${PLAN_COLORS[p] || ''}`}>
                    {p}: {n}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.samples && data.samples.length > 0 && (
        <div className="space-y-4">
          <div className="text-slate-300 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            Previews con datos reales · uno por plan. Cada centro recibirá el correo que corresponda a SU plan.
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {data.samples.map((s) => (
              <Card key={s.plan_level} className="bg-slate-900 border-slate-700 overflow-hidden">
                <CardHeader className="border-b border-slate-800 pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className={`px-2.5 py-1 rounded text-xs font-bold border ${PLAN_COLORS[s.plan_label] || ''}`}>
                      {s.plan_label}
                    </span>
                    <span className="text-xs text-slate-500">{s.sample_email}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    <strong className="text-slate-200">Centro sample:</strong> {s.sample_center_name}
                  </div>
                  <div className="text-xs text-slate-400">
                    <strong className="text-slate-200">Subject:</strong> {s.subject}
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 pt-1">
                    <span>Visitas: {s.metrics?.views ?? 0}</span>
                    <span>WSP: {s.metrics?.wsp ?? 0}</span>
                    <span>Tel: {s.metrics?.tel ?? 0}</span>
                    <span>Cot: {s.metrics?.cot_total ?? 0}</span>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <iframe
                    title={`preview-${s.plan_label}`}
                    srcDoc={s.html}
                    className="w-full bg-white"
                    style={{ height: '720px', border: 0 }}
                    data-testid={`weekly-report-preview-iframe-${s.plan_label.toLowerCase()}`}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-slate-500 flex items-center gap-2 pt-2">
        <MessageCircle className="w-3.5 h-3.5" />
        Cada correo incluye botón WhatsApp verde grande al pie con enlace directo a{' '}
        <span className="text-emerald-400 font-mono">+56 9 2834 1147</span> y mensaje pre-armado por centro.
      </div>
    </div>
  );
}
